import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { interExtrapolateFillGaps, interExtrapolateRefresh } from "api";
import { DateRangePicker, EsgSingleSelectControlledField, InfoTooltip } from "components";
import { useNotify } from "hooks";
import type { Dayjs } from "dayjs";
import { getDateFormatset } from "utils";
import {
    CALCULATION_METHODS_DATA,
    CALCULATION_METHODS_OPTIONS,
    FILL_GAPS_MODAL_DATA,
    REFRESH_EXTRAPOLATION_MODAL_DATA,
} from "./consts";

interface ExtrapolationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaveButtonClick: () => void;
    selectedCollectorIds: string[];
    isFillGapsAction?: boolean;
}

export const ExtrapolationModal = ({
    isOpen,
    onClose,
    onSaveButtonClick,
    selectedCollectorIds = [],
    isFillGapsAction = false,
}: ExtrapolationModalProps) => {
    const notify = useNotify();
    const { mutateAsync: interExtrapolateFillGapsMutation } = useMutation(interExtrapolateFillGaps);
    const { mutateAsync: interExtrapolateRefreshMutation } = useMutation(interExtrapolateRefresh);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const [selectedCalculationMethod, setSelectedCalculationMethod] = useState<{ label: string; value: string } | null>(null);
    const calculationMethodTooltip =
        CALCULATION_METHODS_DATA.find((method) => method.name === selectedCalculationMethod?.value)?.description ?? "";
    const modalDisplayedData = isFillGapsAction ? FILL_GAPS_MODAL_DATA : REFRESH_EXTRAPOLATION_MODAL_DATA;

    const handleClose = () => {
        setFromDate(null);
        setToDate(null);
        setSelectedCalculationMethod(null);
        onClose();
    };

    const handleApply = async () => {
        if (!fromDate || !toDate) return;
        const requestBody = {
            collectorIds: selectedCollectorIds,
            startDate: fromDate.format('YYYY-MM-DD'),
            endDate: toDate.format('YYYY-MM-DD'),
        }
        const apiCall = isFillGapsAction ? interExtrapolateFillGapsMutation : interExtrapolateRefreshMutation;
        try {
            setLoading(true);
            await apiCall(requestBody);
            notify.success("In progress. View details on the Jobs Page.");
            onSaveButtonClick();
            handleClose();
        } catch (error) {
            notify.error("Failed to apply extrapolation.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            fullWidth
        >
            <>
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        color: "#054254",
                        fontWeight: 600,
                        fontSize: "20px",
                        fontFamily: "Manrope"
                    }}
                >
                    {modalDisplayedData.title}
                </DialogTitle>
                <div
                    style={{
                        borderBottom: "1px solid #D9D9D9",
                        width: "100%",
                    }} />

                <DialogContent sx={{ overflowY: "hidden" }}>
                    <Stack mb={2} spacing={1}>
                        <Typography sx={{ fontSize: "16px", }}>{modalDisplayedData.getDescription(selectedCollectorIds.length)}</Typography>
                        <Typography sx={{ fontSize: "16px", }}>Select a date or range that you&apos;d like to apply this to:</Typography>
                        <DateRangePicker
                            viewFromDate={fromDate}
                            viewToDate={toDate}
                            onFromDateChange={setFromDate}
                            onToDateChange={setToDate}
                            fromLabel="From"
                            toLabel="To"
                            format={getDateFormatset()}
                            views={['year', 'month', 'day']}
                            openTo="day"
                            direction="row"
                            maxWidth='400px'
                            enableValidation
                            errorMessage='The "To" date must be after the "From" date.'
                        />
                    </Stack>
                    <Stack spacing={1}>
                        <Typography sx={{ fontSize: "16px", }}>Please select a calculation method:</Typography>
                        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <EsgSingleSelectControlledField
                                options={CALCULATION_METHODS_OPTIONS}
                                selectedValue={selectedCalculationMethod?.value ?? ""}
                                setSelectedValue={(value) => {
                                    const option = CALCULATION_METHODS_OPTIONS.find(
                                        (opt) => opt.value === value || opt.label === value
                                    ) ?? null;
                                    setSelectedCalculationMethod(option);
                                }}
                                placeholder="Select a calculation method"
                                sx={{ width: "265px", minWidth: '45%' }}
                            />
                            {calculationMethodTooltip && <InfoTooltip
                                tooltip={calculationMethodTooltip}
                                width="18"
                                height="18"
                            />}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ marginBottom: "20px", marginRight: "15px" }}>
                    <Button onClick={handleClose} >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApply}
                        variant="contained"
                        color="primary"
                        disabled={loading || !fromDate || !toDate || !selectedCalculationMethod}
                        sx={{ backgroundColor: "#054254" }}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </>
        </Dialog>
    );
};